import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import LoadingSpinner from '../common/LoadingSpinner';
import ImageUpload from '../common/ImageUpload';
import ImageZoomPreviewModal from '../common/ImageZoomPreviewModal';
import mosqueService from '../../services/mosqueService';
import { imageService } from '../../services/imageService';
import { imageFieldUrl } from '../../utils/imageFallback';
import { societyService, extractSocietyResults } from '../../services/societyService';
import { getMosqueUrl, parseSocietyUrl } from '../../utils/urlHelpers';
import SocietySearchableSelect from '../common/SocietySearchableSelect';
import MosquePrayerTimingsBoard from './MosquePrayerTimingsBoard';
import MosqueFeaturesSection from './MosqueFeaturesSection';
import {
  boardHasTimings,
  emptyPrayerTimingsBoard,
  normalizePrayerTimingsBoard,
} from '../../utils/mosquePrayerTimings';
import {
  MOSQUE_FEATURE_KEYS,
  defaultMosqueFeatureValues,
  featureValuesFromMosque,
  mosqueStreetAddressFromStored,
} from '../../utils/mosqueFeatures';

const MosqueCreateForm = ({ mode = 'create', initialMosque = null, societySlug: societySlugProp } = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [societies, setSocieties] = useState([]);
  const [loadingSocieties, setLoadingSocieties] = useState(true);
  const [userSociety, setUserSociety] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [locLoading, setLocLoading] = useState(false);
  const [existingImages, setExistingImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagesToRemove, setImagesToRemove] = useState([]);
  const [displayImageIndex, setDisplayImageIndex] = useState(null);
  const [imagePreview, setImagePreview] = useState(null); // { url, name, revokeOnClose }
  const [prayerTimingsBoard, setPrayerTimingsBoard] = useState(emptyPrayerTimingsBoard);
  const imagePreviewRef = useRef(null);

  const setImagePreviewSafe = useCallback((next) => {
    setImagePreview((prev) => {
      if (prev?.revokeOnClose && prev.url) URL.revokeObjectURL(prev.url);
      return next;
    });
  }, []);

  useEffect(() => {
    imagePreviewRef.current = imagePreview;
  }, [imagePreview]);

  useEffect(() => {
    return () => {
      const p = imagePreviewRef.current;
      if (p?.revokeOnClose && p.url) URL.revokeObjectURL(p.url);
    };
  }, []);

  const isEditMode = mode === 'edit' && initialMosque;

  const [formData, setFormData] = useState({
    name: '',
    type: 'mosque',
    description: '',
    address: '',
    city: '',
    contact_phone: '',
    contact_email: '',
    imam_name: '',
    imam_phone: '',
    capacity: '',
    special_programs: '',
    ...defaultMosqueFeatureValues(),
    images: [],
    society: searchParams.get('society') || '',
    is_public: false,
    latitude: '',
    longitude: '',
  });

  const typeOptions = [
    { value: 'mosque', label: 'Mosque' },
    { value: 'masjid', label: 'Masjid' },
    { value: 'jamia', label: 'Jamia' },
    { value: 'markaz', label: 'Markaz' },
    { value: 'other', label: 'Other' },
  ];

  useEffect(() => {
    const loadSocieties = async () => {
      try {
        setLoadingSocieties(true);
        const userIsAdmin =
          user &&
          (user.is_staff || user.is_superuser || ['admin', 'moderator'].includes(user.role));
        setIsAdmin(!!userIsAdmin);
        const societyIdFromUrl = searchParams.get('society');

        if (userIsAdmin) {
          const response = await societyService.getSocieties({ page_size: 500 });
          const allSocieties = extractSocietyResults(response);
          setSocieties(allSocieties);
          if (societyIdFromUrl) {
            const sid = parseInt(societyIdFromUrl, 10);
            const selected = allSocieties.find((s) => s.id === sid);
            if (selected) {
              setFormData((prev) => ({ ...prev, society: String(selected.id) }));
            }
          }
        } else if (user && user.society) {
          setUserSociety(user.society);
          setSocieties([user.society]);
          setFormData((prev) => ({
            ...prev,
            society: String(user.society.id),
          }));
        } else if (user) {
          const response = await societyService.getSocieties({ page_size: 500 });
          const allSocieties = extractSocietyResults(response);
          setSocieties(allSocieties);
          if (societyIdFromUrl) {
            const sid = parseInt(societyIdFromUrl, 10);
            const selected = allSocieties.find((s) => s.id === sid);
            if (selected) {
              setFormData((prev) => ({ ...prev, society: String(selected.id) }));
            }
          }
        }
      } catch (error) {
        console.error('Error loading societies:', error);
        showError('Failed to load societies. Please try again.');
        setSocieties([]);
      } finally {
        setLoadingSocieties(false);
      }
    };

    if (isAuthenticated && user) {
      loadSocieties();
    } else {
      setLoadingSocieties(false);
    }
  }, [isAuthenticated, user, searchParams, showError]);

  useEffect(() => {
    if (!isEditMode || !initialMosque) {
      setExistingImages([]);
      return;
    }
    const societyRaw = initialMosque.society;
    const sid =
      societyRaw && typeof societyRaw === 'object' ? societyRaw.id : societyRaw;

    const lat =
      initialMosque.latitude !== undefined && initialMosque.latitude !== null
        ? String(initialMosque.latitude)
        : '';
    const lon =
      initialMosque.longitude !== undefined && initialMosque.longitude !== null
        ? String(initialMosque.longitude)
        : '';

    setFormData((prev) => ({
      ...prev,
      name: initialMosque.name || '',
      description: '',
      address: mosqueStreetAddressFromStored(initialMosque.address),
      city: '',
      contact_phone: initialMosque.phone || '',
      imam_name: initialMosque.imam_name || '',
      imam_phone: initialMosque.imam_phone || '',
      society: sid != null ? String(sid) : prev.society,
      is_public: !!initialMosque.is_public,
      latitude: lat,
      longitude: lon,
      images: [],
    }));

    const imgs = Array.isArray(initialMosque.images) ? initialMosque.images : [];
    setExistingImages(
      imgs
        .map((im) => {
          const url = imageFieldUrl(im?.original_image);
          return url && im.id != null ? { id: im.id, url } : null;
        })
        .filter(Boolean),
    );
    setSelectedImages([]);
    setImagesToRemove([]);
    if (imgs.length) {
      const coverId =
        initialMosque.display_image?.id ??
        imgs.find((img) => img.is_featured)?.id;
      if (coverId != null) {
        const idx = imgs.findIndex((img) => img.id === coverId);
        if (idx >= 0) setDisplayImageIndex(idx);
      }
    } else {
      setDisplayImageIndex(null);
    }
    setPrayerTimingsBoard(normalizePrayerTimingsBoard(initialMosque.prayer_timings_board));
    setFormData((prev) => ({
      ...prev,
      ...featureValuesFromMosque(initialMosque),
    }));
  }, [isEditMode, initialMosque]);

  const keptExistingImages = existingImages.filter((img) => !imagesToRemove.includes(img.id));

  const remapDisplayIndexAfterRemove = (removedGlobalIndex) => {
    setDisplayImageIndex((prev) => {
      if (prev === null) return null;
      const remainingCount = keptExistingImages.length - 1 + selectedImages.length;
      if (prev === removedGlobalIndex) {
        return remainingCount > 0 ? 0 : null;
      }
      if (prev > removedGlobalIndex) return prev - 1;
      return prev;
    });
  };

  const resolveSocietySlugForNav = () => {
    if (societySlugProp) return societySlugProp;
    const { isSocietySpecific, societySlug: fromPath } = parseSocietyUrl(location.pathname);
    return isSocietySpecific ? fromPath : null;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleGetCurrentLocation = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      showError('Location is not supported in this browser.');
      return;
    }
    setLocLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setFormData((prev) => ({
          ...prev,
          latitude: pos.coords.latitude.toFixed(8),
          longitude: pos.coords.longitude.toFixed(8),
        }));
        setLocLoading(false);
        showSuccess('Location captured from GPS.');
      },
      (err) => {
        setLocLoading(false);
        const denied = err && (err.code === 1 || err.code === err.PERMISSION_DENIED);
        showError(
          denied
            ? 'Location permission was denied. Enable it in your browser or device settings.'
            : err.message || 'Could not get your location.',
        );
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 60000 },
    );
  }, [showError, showSuccess]);

  const buildAddressForApi = () => {
    const parts = [formData.address?.trim(), formData.city?.trim()].filter(Boolean);
    let base = parts.join(', ');
    const extra = [
      formData.description?.trim(),
      formData.type && formData.type !== 'mosque' ? `Type: ${formData.type}` : '',
      formData.special_programs?.trim(),
    ]
      .filter(Boolean)
      .join(' | ');
    if (extra) {
      base = base ? `${base} — ${extra}` : extra;
    }
    return base.slice(0, 255);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      showError('Please log in to add a mosque');
      return;
    }

    if (!formData.society) {
      showError('Please select a society');
      return;
    }

    setLoading(true);
    try {
      const addressLine = buildAddressForApi();
      const totalImages = keptExistingImages.length + selectedImages.length;
      let coverIndex = displayImageIndex;
      if (totalImages > 0 && coverIndex === null) {
        coverIndex = 0;
      }
      if (coverIndex !== null && coverIndex >= totalImages) {
        coverIndex = 0;
      }

      const payload = {
        name: formData.name.trim(),
        society: formData.society,
        address: addressLine,
        phone: (formData.contact_phone || '').trim(),
        imam_name: (formData.imam_name || '').trim(),
        imam_phone: (formData.imam_phone || '').trim(),
        is_public: !!formData.is_public,
        images: selectedImages,
      };
      MOSQUE_FEATURE_KEYS.forEach((key) => {
        payload[key] = !!formData[key];
      });
      if (coverIndex !== null) {
        payload.display_image_index = coverIndex;
      }
      if (formData.latitude !== '' && formData.longitude !== '') {
        payload.latitude = formData.latitude;
        payload.longitude = formData.longitude;
      }
      if (boardHasTimings(prayerTimingsBoard)) {
        payload.prayer_timings_board = prayerTimingsBoard;
      }

      if (isEditMode && initialMosque) {
        for (const imageId of imagesToRemove) {
          await imageService.deleteImage(imageId);
        }
        const key = initialMosque.slug || initialMosque.id;
        const updated = await mosqueService.updateMosque(key, payload);
        showSuccess('Mosque updated successfully!');
        navigate(getMosqueUrl(updated, resolveSocietySlugForNav()));
        return;
      }

      await mosqueService.createMosque(payload);
      showSuccess('Mosque added successfully!');

      const isSocietyContext = location.pathname.includes('/societies/');
      if (isSocietyContext) {
        const pathParts = location.pathname.split('/');
        const societySlugIndex = pathParts.indexOf('societies') + 1;
        const societySlug = pathParts[societySlugIndex];
        navigate(`/societies/${societySlug}/mosques`);
      } else {
        navigate('/mosques');
      }
    } catch (error) {
      console.error('Error saving mosque:', error);
      const msg =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        error.response?.data?.errors ||
        error.message ||
        (isEditMode ? 'Failed to update mosque. Please try again.' : 'Failed to add mosque. Please try again.');
      showError(typeof msg === 'string' ? msg : isEditMode ? 'Failed to update mosque.' : 'Failed to add mosque.');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-8">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Login Required</h2>
          <p className="text-gray-600 mb-6">
            Please log in to {isEditMode ? 'edit' : 'add'} a mosque.
          </p>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {isEditMode ? 'Edit Mosque' : 'Add Mosque'}
        </h2>
        <p className="text-gray-600 mt-1">
          {isEditMode
            ? 'Update details and photos. New uploads are added to the gallery.'
            : 'Add a mosque or prayer facility to your community'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Mosque Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter mosque name"
            />
          </div>

          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-2">
              Type *
            </label>
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {typeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Description {isEditMode ? '' : '*'}
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            required={!isEditMode}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Describe the mosque, its history, and community"
          />
          <p className="mt-1 text-xs text-gray-500">
            Optional on edit. Address and programs are saved separately from mosque features below.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
              Address *
            </label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter full address"
            />
          </div>

          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
              City {isEditMode ? '' : '*'}
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              required={!isEditMode}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter city"
            />
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <span className="block text-sm font-medium text-gray-900">Location on map (optional)</span>
            </div>
            <button
              type="button"
              onClick={handleGetCurrentLocation}
              disabled={locLoading}
              className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {locLoading ? 'Getting location…' : 'Get current location'}
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="latitude" className="block text-sm font-medium text-gray-700 mb-1">
                Latitude <span className="font-normal text-gray-500">(read-only)</span>
              </label>
              <input
                type="text"
                id="latitude"
                name="latitude"
                value={formData.latitude}
                readOnly
                tabIndex={-1}
                autoComplete="off"
                aria-readonly="true"
                placeholder="Use “Get current location”"
                className="w-full cursor-default px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-900 focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="longitude" className="block text-sm font-medium text-gray-700 mb-1">
                Longitude <span className="font-normal text-gray-500">(read-only)</span>
              </label>
              <input
                type="text"
                id="longitude"
                name="longitude"
                value={formData.longitude}
                readOnly
                tabIndex={-1}
                autoComplete="off"
                aria-readonly="true"
                placeholder="Use “Get current location”"
                className="w-full cursor-default px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-900 focus:outline-none"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="contact_phone" className="block text-sm font-medium text-gray-700 mb-2">
              Contact Phone
            </label>
            <input
              type="tel"
              id="contact_phone"
              name="contact_phone"
              value={formData.contact_phone}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Phone number"
            />
          </div>

          <div>
            <label htmlFor="contact_email" className="block text-sm font-medium text-gray-700 mb-2">
              Contact Email
            </label>
            <input
              type="email"
              id="contact_email"
              name="contact_email"
              value={formData.contact_email}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Email address"
            />
            <p className="mt-1 text-xs text-gray-500">Not stored on the server yet; for your notes only.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="imam_name" className="block text-sm font-medium text-gray-700 mb-2">
              Imam Name
            </label>
            <input
              type="text"
              id="imam_name"
              name="imam_name"
              value={formData.imam_name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Imam's name"
            />
          </div>

          <div>
            <label htmlFor="imam_phone" className="block text-sm font-medium text-gray-700 mb-2">
              Imam Phone
            </label>
            <input
              type="tel"
              id="imam_phone"
              name="imam_phone"
              value={formData.imam_phone}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Imam phone"
            />
          </div>

          <div className="md:col-span-2">
            <label htmlFor="capacity" className="block text-sm font-medium text-gray-700 mb-2">
              Prayer Capacity
            </label>
            <input
              type="number"
              id="capacity"
              name="capacity"
              value={formData.capacity}
              onChange={handleInputChange}
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Number of people"
            />
            <p className="mt-1 text-xs text-gray-500">For display only until the API stores it.</p>
          </div>
        </div>

        <MosqueFeaturesSection
          editable
          values={formData}
          onToggle={(key, checked) =>
            setFormData((prev) => ({ ...prev, [key]: checked }))
          }
          className="!mb-0 border-gray-200"
        />

        <MosquePrayerTimingsBoard
          embedded
          value={prayerTimingsBoard}
          onChange={setPrayerTimingsBoard}
          className="border border-gray-200 rounded-lg p-4 bg-gray-50"
        />

        <div>
          <label htmlFor="special_programs" className="block text-sm font-medium text-gray-700 mb-2">
            Special Programs
          </label>
          <textarea
            id="special_programs"
            name="special_programs"
            value={formData.special_programs}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Quran classes, Islamic lectures, community events, etc."
          />
        </div>

        {isAdmin ? (
          <SocietySearchableSelect
            societies={societies}
            value={formData.society}
            onChange={handleInputChange}
            loading={loadingSocieties}
          />
        ) : userSociety ? (
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-2">Society</span>
            <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
              {userSociety.name}
              {userSociety.city ? ` — ${userSociety.city}` : ''}
            </div>
            <p className="mt-1 text-xs text-gray-500">Listings are tied to your society.</p>
          </div>
        ) : (
          <SocietySearchableSelect
            societies={societies}
            value={formData.society}
            onChange={handleInputChange}
            loading={loadingSocieties}
            helperText="Choose which society this mosque belongs to. Use the signup flow if yours is missing."
          />
        )}

        <div>
          {isEditMode && keptExistingImages.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current images (set cover or remove)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {keptExistingImages.map((img, index) => {
                  const isCover = displayImageIndex === index;
                  return (
                    <div key={img.id} className="relative group">
                      <img
                        src={img.url}
                        alt=""
                        className={`w-full h-24 object-cover rounded-lg border-2 ${
                          isCover ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setDisplayImageIndex(index)}
                        disabled={loading}
                        className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                          isCover
                            ? 'bg-blue-600 text-white'
                            : 'bg-white/90 text-gray-700 hover:bg-white'
                        }`}
                      >
                        {isCover ? 'Cover' : 'Set cover'}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setImagePreviewSafe({
                            url: img.url,
                            name: `Photo on file ${index + 1}`,
                            revokeOnClose: false,
                          })
                        }
                        className="absolute bottom-1 left-1 rounded bg-black/55 px-2 py-0.5 text-[11px] text-white hover:bg-black/70"
                      >
                        Zoom
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setImagesToRemove((prev) => [...prev, img.id]);
                          remapDisplayIndexAfterRemove(index);
                        }}
                        disabled={loading}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 disabled:opacity-50"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <ImageUpload
            selectedImages={selectedImages}
            onImagesChange={setSelectedImages}
            maxImages={Math.max(0, 5 - keptExistingImages.length)}
            disabled={loading}
            autoCompress
            displayImageIndex={displayImageIndex}
            onDisplayImageIndexChange={setDisplayImageIndex}
            imageIndexOffset={keptExistingImages.length}
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="is_public"
            name="is_public"
            checked={formData.is_public}
            onChange={handleInputChange}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="is_public" className="ml-2 block text-sm text-gray-700">
            Make this mosque visible to all users (not just society members)
          </label>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 pt-6">
          <button
            type="submit"
            disabled={loading || loadingSocieties}
            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? <LoadingSpinner size="sm" /> : isEditMode ? 'Save changes' : 'Add Mosque'}
          </button>
          <button
            type="button"
            onClick={() => {
              const isSocietyContext = location.pathname.includes('/societies/');
              if (isSocietyContext) {
                const pathParts = location.pathname.split('/');
                const societySlugIndex = pathParts.indexOf('societies') + 1;
                const societySlug = pathParts[societySlugIndex];
                navigate(`/societies/${societySlug}/mosques`);
              } else {
                navigate('/mosques');
              }
            }}
            className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>

      <ImageZoomPreviewModal
        isOpen={!!imagePreview}
        imageUrl={imagePreview?.url}
        imageName={imagePreview?.name}
        onClose={() => setImagePreviewSafe(null)}
      />
    </div>
  );
};

export default MosqueCreateForm;
